import { useState, useEffect } from 'react';
import { X, DownloadSimple } from '@phosphor-icons/react';
import api from '@/api';

/**
 * Preview a PDF inline inside a modal before downloading.
 * Fetches the PDF as a blob and renders it in an iframe, then offers a download button.
 */
export default function ReportPreviewModal({ open, path, title = 'Report Preview', filename = 'report.pdf', onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !path) return;
    let revoked = null;
    setLoading(true);
    setError(null);
    setUrl(null);
    api.get(path, { responseType: 'blob' })
      .then(resp => {
        const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        revoked = blobUrl;
        setUrl(blobUrl);
      })
      .catch(() => setError('Failed to load preview'))
      .finally(() => setLoading(false));
    return () => { if (revoked) window.URL.revokeObjectURL(revoked); };
  }, [open, path]);

  const handleDownload = () => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="report-preview-modal">
      <div className="bg-white rounded-md border border-[#E4E4E7] shadow-xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E4E7] bg-[#F7F7F8]">
          <div>
            <h2 className="font-heading text-base font-semibold text-[#0A0A0A]">{title}</h2>
            <p className="text-xs text-[#A1A1AA]">Preview before downloading</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={!url}
              data-testid="report-preview-download"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#0052FF] text-white rounded hover:bg-[#0040CC] disabled:opacity-50"
            >
              <DownloadSimple size={14} /> Download
            </button>
            <button
              onClick={onClose}
              data-testid="report-preview-close"
              className="p-1.5 rounded hover:bg-[#E4E4E7]"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-[#F0F0F1] overflow-hidden">
          {loading && <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" /></div>}
          {error && <div className="flex items-center justify-center h-full text-sm text-red-600">{error}</div>}
          {url && !loading && (
            <iframe src={url} title="Report preview" className="w-full h-full border-0" data-testid="report-preview-iframe" />
          )}
        </div>
      </div>
    </div>
  );
}
